import { useEffect, useState } from "react";
import { X, FileArrowDown, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { isFolderSupported, saveBlobToFolder, getFolderName } from "@/lib/localFolder";

// Preview a document by fetching as blob (auth) and rendering inline.
// Supports: images (jpg/png/webp/gif), pdf. Others show "no preview" + download button.
export default function PreviewModal({ doc, onClose, subfolder }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [folderName, setFolderName] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
        setBlobUrl(URL.createObjectURL(data));
      } catch (e) {
        setErr("Failed to load preview");
      }
    })();
    getFolderName().then(setFolderName);
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line
  }, [doc.id]);

  const download = () => {
    const token = localStorage.getItem("access_token");
    window.open(`${API}/documents/${doc.id}/download${token ? `?auth=${encodeURIComponent(token)}` : ""}`, "_blank");
  };

  const saveLocal = async () => {
    if (!blobUrl) return;
    setSaving(true);
    try {
      const resp = await fetch(blobUrl);
      const b = await resp.blob();
      const saved = await saveBlobToFolder(doc.original_filename, b, subfolder);
      toast.success(`Saved to your folder as ${saved}`);
    } catch (e) {
      toast.error(e.message || "Save failed");
    } finally { setSaving(false); }
  };

  const ct = doc.content_type || "";
  const isImage = ct.startsWith("image/");
  const isPdf = ct === "application/pdf";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="preview-modal">
      <div className="bg-white border border-zinc-200 shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Preview</div>
            <div className="text-sm font-medium text-zinc-900 truncate">{doc.original_filename}</div>
          </div>
          <div className="flex items-center gap-2">
            {isFolderSupported() && folderName && (
              <button onClick={saveLocal} disabled={saving || !blobUrl}
                className="text-xs bg-zinc-100 border border-zinc-200 px-3 py-1.5 hover:bg-zinc-200 flex items-center gap-1 disabled:opacity-60"
                data-testid="preview-save-local-btn"
                title={`Save to your folder (${folderName})`}>
                <FloppyDisk size={14} /> {saving ? "Saving…" : `Save to ${folderName}`}
              </button>
            )}
            <button onClick={download}
              className="text-xs bg-white border border-zinc-200 px-3 py-1.5 hover:bg-zinc-100 flex items-center gap-1"
              data-testid="preview-download-btn">
              <FileArrowDown size={14} /> Download
            </button>
            <button onClick={onClose} data-testid="preview-close-btn"
              className="text-zinc-500 hover:text-zinc-900 p-1.5" aria-label="Close">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 bg-zinc-100 overflow-auto flex items-center justify-center">
          {err ? (
            <div className="text-sm text-red-700 font-mono">{err}</div>
          ) : !blobUrl ? (
            <div className="text-sm text-zinc-500 font-mono">Loading…</div>
          ) : isImage ? (
            <img src={blobUrl} alt={doc.original_filename} className="max-w-full max-h-full object-contain" />
          ) : isPdf ? (
            <iframe title={doc.original_filename} src={blobUrl} className="w-full h-full bg-white" />
          ) : (
            <div className="text-center p-8">
              <div className="text-sm text-zinc-600 mb-2">No inline preview for this file type.</div>
              <div className="text-xs text-zinc-500 font-mono">{ct}</div>
              <button onClick={download} className="mt-4 bg-[#002FA7] text-white text-sm px-4 py-2 hover:bg-[#00227A]">
                Download instead
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
